// Code generated by apic; DO NOT EDIT.
import type { McpToolMeta } from "../types";
import type { SampleContext } from "./shared";
import { shellEscapeSQ } from "./curl";
import { goStringLit } from "./go";
import { pyStringLit } from "./python";
import { tsStringLit } from "./typescript";

/**
 * MCP sample context: carries the base URL, auth credentials, and per-tool arguments.
 * MCP auth is at the session/transport level (not per-tool), so auth is carried once.
 */
export interface McpSampleContext {
  /** Base URL of the service (e.g. "https://api.example.com"). */
  baseUrl: string;
  /** Bearer token for JWT auth. */
  token?: string;
  /** API key for api_key auth (HMAC signing is required; see comment). */
  apiKey?: string;
  /** Arguments to pass to the tool (keyed by argument name). */
  arguments?: Record<string, unknown>;
}

/**
 * Build a JSON-RPC 2.0 tools/call envelope for the given tool name and args.
 * Matches the MCP spec (2025-06-18): params.arguments is the named-arg map.
 */
function buildToolCallEnvelope(
  name: string,
  args: Record<string, unknown>,
  id = 1,
): Record<string, unknown> {
  return {
    jsonrpc: "2.0",
    id,
    method: "tools/call",
    params: {
      name,
      arguments: args,
    },
  };
}

// ── builders ──────────────────────────────────────────────────────────────────

/**
 * mcpCurlSample returns a curl command for invoking an MCP tool.
 *
 * Always POSTs to <baseUrl>/mcp with Content-Type: application/json.
 * Auth:
 *  - jwt  → Authorization: Bearer <token>
 *  - api_key → comment only; HMAC signing cannot be performed from CLI
 *  - public/none → no auth header
 */
export function mcpCurlSample(
  tool: McpToolMeta,
  ctx: McpSampleContext,
  mcpAuthMode = "none",
): string {
  const base = ctx.baseUrl.replace(/\/$/, "");
  const url = `${base}/mcp`;
  const args = ctx.arguments !== undefined ? ctx.arguments : {};
  const envelope = buildToolCallEnvelope(tool.name, args);
  const body = JSON.stringify(envelope);

  const lines: string[] = [];
  lines.push(`curl -s \\`);
  lines.push(`  -X POST \\`);
  lines.push(`  '${shellEscapeSQ(url)}' \\`);
  lines.push(`  -H 'Content-Type: application/json' \\`);
  lines.push(`  -H 'Accept: application/json' \\`);

  if (mcpAuthMode === "api_key") {
    lines.push(`  # api_key/HMAC auth required: Authorization: Bearer <hmac-hex>`);
    lines.push(`  # plus X-Nonce and X-Timestamp headers. Cannot be performed from curl.`);
    lines.push(`  # Use the generated SDK or CLI instead.`);
  } else if (mcpAuthMode === "jwt" || mcpAuthMode === "jwt+api_key") {
    const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
    lines.push(`  -H 'Authorization: Bearer ${shellEscapeSQ(tok)}' \\`);
  }

  lines.push(`  -d '${shellEscapeSQ(body)}'`);

  return lines.join("\n");
}

/**
 * mcpGoSample returns a Go net/http snippet for invoking an MCP tool.
 */
export function mcpGoSample(
  tool: McpToolMeta,
  ctx: McpSampleContext,
  mcpAuthMode = "none",
): string {
  const base = ctx.baseUrl.replace(/\/$/, "");
  const url = `${base}/mcp`;
  const args = ctx.arguments !== undefined ? ctx.arguments : {};
  const envelope = buildToolCallEnvelope(tool.name, args);
  const bodyJSON = JSON.stringify(envelope, null, "\t");
  const escaped = bodyJSON.replace(/`/g, "` + \"`\" + `");

  const lines: string[] = [];
  lines.push(`package main`);
  lines.push(``);
  lines.push(`import (`);
  lines.push(`\t"bytes"`);
  lines.push(`\t"fmt"`);
  lines.push(`\t"net/http"`);
  lines.push(`)`);
  lines.push(``);
  lines.push(`func main() {`);
  lines.push(`\tbody := []byte(\`` + escaped + `\`)`);
  lines.push(`\treq, _ := http.NewRequest("POST", ${goStringLit(url)}, bytes.NewReader(body))`);
  lines.push(`\treq.Header.Set("Content-Type", "application/json")`);
  lines.push(`\treq.Header.Set("Accept", "application/json")`);

  if (mcpAuthMode === "api_key") {
    lines.push(`\t// api_key/HMAC auth: Authorization: Bearer <hmac-hex>`);
    lines.push(`\t// also requires X-Nonce and X-Timestamp headers with correct HMAC signature.`);
    lines.push(`\t// Use the generated apic client which handles signing automatically.`);
  } else if (mcpAuthMode === "jwt" || mcpAuthMode === "jwt+api_key") {
    const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
    lines.push(`\treq.Header.Set("Authorization", ${goStringLit("Bearer " + tok)})`);
  }

  lines.push(``);
  lines.push(`\tclient := &http.Client{}`);
  lines.push(`\tresp, err := client.Do(req)`);
  lines.push(`\tif err != nil {`);
  lines.push(`\t\tpanic(err)`);
  lines.push(`\t}`);
  lines.push(`\tdefer resp.Body.Close()`);
  lines.push(`\tfmt.Println(resp.Status)`);
  lines.push(`}`);

  return lines.join("\n");
}

/**
 * mcpPythonSample returns a Python requests snippet for invoking an MCP tool.
 */
export function mcpPythonSample(
  tool: McpToolMeta,
  ctx: McpSampleContext,
  mcpAuthMode = "none",
): string {
  const base = ctx.baseUrl.replace(/\/$/, "");
  const url = `${base}/mcp`;
  const args = ctx.arguments !== undefined ? ctx.arguments : {};
  const envelope = buildToolCallEnvelope(tool.name, args);

  // Serialize the envelope as a JSON string and use json.loads() in the emitted
  // Python so booleans/null serialize correctly (JSON true/false/null → Python
  // True/False/None). pyStringLit produces a valid Python double-quoted string
  // literal. (R2-3 fix.)
  const payloadLit = `json.loads(${pyStringLit(JSON.stringify(envelope))})`;

  const lines: string[] = [];
  lines.push(`import json`);
  lines.push(`import requests`);
  lines.push(``);
  lines.push(`url = ${pyStringLit(url)}`);
  lines.push(`headers = {"Content-Type": "application/json", "Accept": "application/json"}`);
  lines.push(`payload = ${payloadLit}`);

  if (mcpAuthMode === "api_key") {
    lines.push(``);
    lines.push(`# api_key/HMAC auth required: cannot be performed without the apic SDK.`);
    lines.push(`# The SDK computes HMAC-SHA256 over nonce+timestamp+body with your API key.`);
    lines.push(`# Use the generated CLI or SDK to invoke MCP tools with api_key auth.`);
  } else if (mcpAuthMode === "jwt" || mcpAuthMode === "jwt+api_key") {
    const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
    lines.push(`headers["Authorization"] = ${pyStringLit("Bearer " + tok)}`);
  }

  lines.push(``);
  lines.push(`resp = requests.post(url, headers=headers, json=payload)`);
  lines.push(`resp.raise_for_status()`);
  lines.push(`print(resp.json())`);

  return lines.join("\n");
}

/**
 * mcpTsSample returns a TypeScript snippet for invoking an MCP tool.
 *
 * Always uses a raw fetch POST (MCP does not have named per-tool client methods
 * in the standard generated client). Auth is injected per mcpAuthMode.
 */
export function mcpTsSample(
  tool: McpToolMeta,
  ctx: McpSampleContext,
  mcpAuthMode = "none",
): string {
  const base = ctx.baseUrl.replace(/\/$/, "");
  const url = `${base}/mcp`;
  const args = ctx.arguments !== undefined ? ctx.arguments : {};
  const envelope = buildToolCallEnvelope(tool.name, args);

  const lines: string[] = [];
  lines.push(`async function main() {`);
  lines.push(`  const url = ${tsStringLit(url)};`);
  lines.push(`  const headers: Record<string, string> = {`);
  lines.push(`    "Content-Type": "application/json",`);
  lines.push(`    "Accept": "application/json",`);

  if (mcpAuthMode === "api_key") {
    lines.push(`    // api_key/HMAC auth: Authorization: Bearer <hmac-hex>`);
    lines.push(`    // Requires X-Nonce + X-Timestamp + correct HMAC-SHA256 signature.`);
    lines.push(`    // Use the generated apic client which computes the signature automatically.`);
  } else if (mcpAuthMode === "jwt" || mcpAuthMode === "jwt+api_key") {
    const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
    lines.push(`    "Authorization": ${tsStringLit("Bearer " + tok)},`);
  }

  lines.push(`  };`);
  lines.push(``);
  lines.push(`  const body = ${JSON.stringify(envelope, null, 2).replace(/\n/g, "\n  ")};`);
  lines.push(``);
  lines.push(`  const resp = await fetch(url, {`);
  lines.push(`    method: "POST",`);
  lines.push(`    headers,`);
  lines.push(`    body: JSON.stringify(body),`);
  lines.push(`    credentials: "same-origin",`);
  lines.push(`  });`);
  lines.push(``);
  lines.push(`  const data: unknown = await resp.json();`);
  lines.push(`  console.log(data);`);
  lines.push(`}`);
  lines.push(``);
  lines.push(`main().catch(console.error);`);

  return lines.join("\n");
}

// Re-export SampleContext for convenience.
export type { SampleContext } from "./shared";
